import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import AdminHeader from '../components/AdminHeader';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import '../adminSide/customStyles.css';
import { toast } from 'react-toastify';

function OfferUpdate() {
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [items, setItems] = useState([]);
    const [selectedItem, setSelectedItem] = useState('');
    const [selectedItemObj, setSelectedItemObj] = useState(null);
    const [offerPrice, setOfferPrice] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

    useEffect(() => {
        fetch(`${apiBaseUrl}/getCategories/BLACKBATON_2526`)
            .then(response => response.json())
            .then(data => setCategories(data));
    }, [apiBaseUrl]);

    useEffect(() => {
        if (selectedCategory) {
            fetch(`${apiBaseUrl}/getProductByCategory/BLACKBATON_2526?Id=${selectedCategory}`)
                .then(response => response.json())
                .then(data => setItems(data.map(item => ({
                    Id: item.ID,
                    ItemName: item.ItemName.toUpperCase(),
                    MRP: item.MRP,
                }))));
        }
    }, [selectedCategory, apiBaseUrl]);

    const handleCategoryChange = (selectedOption) => {
        setSelectedCategory(selectedOption ? selectedOption.value : '');
        setSelectedItem('');
        setSelectedItemObj(null);
    };

    const handleItemChange = (selectedOption) => {
        const itemId = selectedOption ? selectedOption.value : '';
        setSelectedItem(itemId);
        const itemObj = items.find(item => item.Id === itemId);
        setSelectedItemObj(itemObj || null);
    };

    const handleSubmit = async () => {
        if (!selectedItemObj || !offerPrice) {
            toast.info('Please select an item and enter an offer price.');
            return;
        }

        setIsSubmitting(true);
        const payload = {
            categoryName: categories.find(cat => cat.Id === selectedCategory)?.Name || '',
            itemId: selectedItemObj.Id,
            itemName: selectedItemObj.ItemName,
            mrp: selectedItemObj.MRP,
            offerPrice: offerPrice
        };

        try {
            const response = await fetch(`${apiBaseUrl}/addOffer`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (response.ok) {
                toast.info('Offer added successfully!');
                setSelectedCategory('');
                setSelectedItem('');
                setSelectedItemObj(null);
                setOfferPrice('');
            } else {
                toast.info(data.error || 'Something went wrong.');
            }
        } catch (error) {
            console.error('Error submitting offer:', error);
            toast.info('Server error.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const selectStyles = {
        control: (base, state) => ({
            ...base,
            borderRadius: '12px',
            borderColor: state.isFocused ? '#000' : '#e5e7eb',
            boxShadow: state.isFocused ? '0 0 0 1px #000' : 'none',
            '&:hover': {
                borderColor: state.isFocused ? '#000' : '#d1d5db',
            },
            padding: '2px',
            fontSize: '14px',
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: '500'
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#000' : state.isFocused ? '#f3f4f6' : '#fff',
            color: state.isSelected ? '#fff' : '#000',
            '&:active': {
                backgroundColor: '#333',
            },
            fontSize: '14px',
            fontFamily: 'Montserrat, sans-serif'
        })
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col font-dmSans">
            <AdminHeader />

            <div className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="w-full max-w-2xl bg-white shadow-2xl rounded-2xl border border-gray-100 overflow-hidden animate-in">
                    <div className="bg-black py-8 px-6">
                        <h1 className="text-3xl font-extrabold text-center text-white font-montserrat tracking-tight">ADD OFFER</h1>
                        <p className="text-gray-400 text-center mt-2 text-sm uppercase tracking-widest font-medium">Create Promotional Pricing</p>
                    </div>

                    <div className="p-8 space-y-8">
                        {/* Category Selection */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-gray-400 tracking-widest uppercase ml-1">Select Category</label>
                            <Select
                                onChange={handleCategoryChange}
                                options={categories.map(category => ({ value: category.Id, label: category.Name.toUpperCase() }))}
                                styles={selectStyles}
                                placeholder="Choose a category..."
                                isClearable
                                isSearchable
                            />
                        </div>

                        {/* Item Selection */}
                        {selectedCategory && (
                            <div className="space-y-8 animate-in">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-gray-400 tracking-widest uppercase ml-1">Select Item</label>
                                    <Select
                                        onChange={handleItemChange}
                                        options={items.map(item => ({ value: item.Id, label: item.ItemName }))}
                                        styles={selectStyles}
                                        placeholder="Pick an item..."
                                        isClearable
                                        isSearchable
                                    />
                                </div>

                                {selectedItemObj && (
                                    <div className="space-y-6 bg-gray-50 p-6 rounded-xl border border-gray-100 animate-in">
                                        <div className="flex justify-between items-center">
                                            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Original Price</span>
                                            <span className="text-xl font-bold text-black font-montserrat">?{selectedItemObj.MRP}</span>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-[10px] font-bold text-gray-400 tracking-widest uppercase ml-1">Offer Price</label>
                                            <div className="relative">
                                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">?</span>
                                                <input
                                                    type="number"
                                                    className="w-full pl-8 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-black focus:border-black transition-all font-montserrat font-semibold"
                                                    value={offerPrice}
                                                    onChange={(e) => setOfferPrice(e.target.value)}
                                                    placeholder="Enter new price"
                                                />
                                            </div>
                                        </div>

                                        <button
                                            onClick={handleSubmit}
                                            disabled={isSubmitting}
                                            className={`w-full py-4 px-6 rounded-xl font-bold uppercase tracking-widest text-sm transition-all duration-300 shadow-lg 
                                                ${isSubmitting
                                                    ? 'bg-gray-400 cursor-not-allowed'
                                                    : 'bg-black text-white hover:bg-gray-900 active:scale-[0.98] hover:shadow-black/20'}`}
                                        >
                                            {isSubmitting ? (
                                                <div className="flex items-center justify-center gap-2">
                                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                                    <span>Processing...</span>
                                                </div>
                                            ) : 'Apply Offer'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {!selectedCategory && (
                            <div className="py-12 flex flex-col items-center justify-center text-gray-300 space-y-4 border-2 border-dashed border-gray-100 rounded-2xl">
                                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                                <p className="text-xs font-bold tracking-widest uppercase">Start by selecting a category</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default OfferUpdate;




